/**
 * Demographic Value Map Auto-Detection
 * 
 * Comprehensive token/alias sets mirroring Python's personas.py and name_synthesizer.py.
 * Auto-detects mappings for gender, age, country, ethnicity using multilingual tokens.
 */

import { AGE_BELOW_FIRST_BAND_LABEL } from '@digital-twins/shared-types'

/** Youngest integer age that is a real age, not an SPSS-style code (1, 2, 3). */
const UNDER_18_MIN_AGE = 13

const AGE_RANGE_PATTERN = /^(\d{1,3})\s*[-–]\s*(\d{1,3})$/

// ============================================================================
// Gender Tokens (mirrors personas.py)
// ============================================================================

/** Machine key for null / missing in value maps; must match Python `VALUE_MAP_BLANK_SENTINEL`. */
export const VALUE_MAP_BLANK_SENTINEL = '__MISSING__'

/** Stored in value_map / API; must match Python `VALUE_MAP_RANDOM_TARGET`. */
export const VALUE_MAP_RANDOM_TARGET = 'Random'

/** Frontend-only label for `VALUE_MAP_RANDOM_TARGET` in mapping UI. */
export const VALUE_MAP_RANDOM_DISPLAY_LABEL = 'Sample from distribution'

const MALE_TOKENS = new Set([
  'male', 'm', 'man', 'boy',
  // Spanish
  'hombre', 'masculino',
  // German
  'männlich', 'mann',
  // French
  'homme', 'masculin',
  // Portuguese
  'homem',
  // Italian
  'uomo', 'maschio',
  // Turkish
  'erkek',
  // Dutch
  'mannelijk',
  // Chinese/Japanese
  '男', '男性',
  // Hebrew
  'זכר',
  // Arabic
  'ذكر',
])

const FEMALE_TOKENS = new Set([
  'female', 'f', 'woman', 'girl',
  // Spanish
  'mujer', 'femenino',
  // German
  'weiblich', 'frau',
  // French
  'femme', 'féminin',
  // Portuguese
  'mulher', 'feminino',
  // Italian
  'donna', 'femmina',
  // Turkish
  'kadın',
  // Dutch
  'vrouw', 'vrouwelijk',
  // Chinese/Japanese
  '女', '女性',
  // Hebrew
  'נקבה',
  // Arabic
  'أنثى',
])

const NON_BINARY_TOKENS = new Set([
  'non-binary', 'nonbinary', 'non binary', 'nb',
  'genderqueer', 'genderfluid', 'gender fluid',
  'agender', 'bigender', 'two-spirit', 'two spirit',
  'pangender', 'androgynous', 'third gender',
  // Spanish
  'no binario', 'no-binario', 'género no binario',
  // German
  'nicht-binär', 'nicht binär', 'divers',
  // French
  'non-binaire', 'non binaire',
  // Portuguese
  'não-binário', 'não binário', 'não-binária',
  // Italian
  'non binario',
  // Dutch
  'niet-binair',
])

// ============================================================================
// Country Aliases (mirrors name_synthesizer.py COUNTRY_ALIASES)
// ============================================================================

const COUNTRY_ALIASES: Record<string, string> = {
  // English-speaking
  'us': 'US', 'usa': 'US', 'united states': 'US', 'united states of america': 'US', 'america': 'US',
  'uk': 'GB', 'gbr': 'GB', 'united kingdom': 'GB', 'great britain': 'GB', 'england': 'GB', 'gb': 'GB',
  'australia': 'AU', 'au': 'AU', 'aus': 'AU',
  'canada': 'CA', 'ca': 'CA', 'can': 'CA',
  'ireland': 'IE', 'ie': 'IE', 'irl': 'IE',
  'new zealand': 'NZ', 'nz': 'NZ', 'nzl': 'NZ',
  'south africa': 'ZA', 'za': 'ZA', 'zaf': 'ZA',
  'nigeria': 'NG', 'ng': 'NG', 'nga': 'NG',
  'philippines': 'PH', 'ph': 'PH', 'phl': 'PH',
  'kenya': 'KE', 'ke': 'KE', 'ken': 'KE',
  'pakistan': 'PK', 'pk': 'PK', 'pak': 'PK',
  'singapore': 'SG', 'sg': 'SG', 'sgp': 'SG',
  'malaysia': 'MY', 'my': 'MY', 'mys': 'MY',
  // Latin America & Iberia
  'brazil': 'BR', 'brasil': 'BR', 'br': 'BR', 'bra': 'BR',
  'portugal': 'PT', 'pt': 'PT', 'prt': 'PT',
  'mexico': 'MX', 'méxico': 'MX', 'mx': 'MX', 'mex': 'MX',
  'estados unidos': 'US',
  'reino unido': 'GB',
  'spain': 'ES', 'españa': 'ES', 'es': 'ES', 'esp': 'ES',
  'argentina': 'AR', 'ar': 'AR', 'arg': 'AR',
  'colombia': 'CO', 'co': 'CO', 'col': 'CO',
  'chile': 'CL', 'cl': 'CL', 'chl': 'CL',
  'peru': 'PE', 'perú': 'PE', 'pe': 'PE', 'per': 'PE',
  // Europe
  'germany': 'DE', 'deutschland': 'DE', 'de': 'DE', 'deu': 'DE',
  'france': 'FR', 'fr': 'FR', 'fra': 'FR',
  'italy': 'IT', 'italia': 'IT', 'it': 'IT', 'ita': 'IT',
  'netherlands': 'NL', 'holland': 'NL', 'nl': 'NL', 'nld': 'NL',
  'sweden': 'SE', 'se': 'SE', 'sverige': 'SE', 'swe': 'SE',
  'poland': 'PL', 'polska': 'PL', 'pl': 'PL', 'pol': 'PL',
  'turkey': 'TR', 'türkiye': 'TR', 'tr': 'TR', 'tur': 'TR',
  'russia': 'RU', 'россия': 'RU', 'ru': 'RU', 'rus': 'RU',
  'czech republic': 'CZ', 'czechia': 'CZ', 'cz': 'CZ', 'cze': 'CZ',
  'austria': 'AT', 'at': 'AT', 'aut': 'AT',
  'switzerland': 'CH', 'ch': 'CH', 'che': 'CH',
  'belgium': 'BE', 'be': 'BE', 'bel': 'BE',
  'denmark': 'DK', 'dk': 'DK', 'dnk': 'DK',
  'norway': 'NO', 'no': 'NO', 'nor': 'NO',
  'finland': 'FI', 'fi': 'FI', 'fin': 'FI',
  'greece': 'GR', 'gr': 'GR', 'grc': 'GR',
  'romania': 'RO', 'ro': 'RO', 'rou': 'RO',
  'hungary': 'HU', 'hu': 'HU', 'hun': 'HU',
  'ukraine': 'UA', 'ua': 'UA', 'ukr': 'UA',
  'croatia': 'HR', 'hr': 'HR', 'hrv': 'HR',
  'slovakia': 'SK', 'sk': 'SK', 'svk': 'SK',
  'slovenia': 'SI', 'si': 'SI', 'svn': 'SI',
  'bulgaria': 'BG', 'bg': 'BG', 'bgr': 'BG',
  'lithuania': 'LT', 'lt': 'LT', 'ltu': 'LT',
  'latvia': 'LV', 'lv': 'LV', 'lva': 'LV',
  'estonia': 'EE', 'ee': 'EE', 'est': 'EE',
  'iceland': 'IS', 'is': 'IS', 'isl': 'IS',
  // Asia
  'japan': 'JP', 'jp': 'JP', 'jpn': 'JP',
  'china': 'CN', 'cn': 'CN', 'chn': 'CN',
  'south korea': 'KR', 'korea': 'KR', 'kr': 'KR', 'kor': 'KR',
  'india': 'IN', 'in': 'IN', 'ind': 'IN',
  'thailand': 'TH', 'th': 'TH', 'tha': 'TH',
  'indonesia': 'ID', 'id': 'ID', 'idn': 'ID',
  'vietnam': 'VN', 'vn': 'VN', 'vnm': 'VN',
  'taiwan': 'TW', 'tw': 'TW', 'twn': 'TW',
  'hong kong': 'HK', 'hk': 'HK', 'hkg': 'HK',
  // Middle East & Africa
  'saudi arabia': 'SA', 'sa': 'SA', 'sau': 'SA',
  'egypt': 'EG', 'eg': 'EG', 'egy': 'EG',
  'uae': 'AE', 'united arab emirates': 'AE', 'ae': 'AE', 'are': 'AE',
  'israel': 'IL', 'il': 'IL', 'isr': 'IL',
  'iran': 'IR', 'ir': 'IR', 'irn': 'IR',
}

// ============================================================================
// Ethnicity Aliases (comprehensive multilingual)
// ============================================================================

const ETHNICITY_ALIASES: Record<string, string> = {
  // White/Caucasian
  'white': 'White',
  'caucasian': 'White',
  'blanco': 'White',  // Spanish
  'branco': 'White',  // Portuguese
  'blanc': 'White',   // French
  'weiß': 'White',    // German
  'bianco': 'White',  // Italian
  'beyaz': 'White',   // Turkish
  // Black/African American
  'black': 'Black or African American',
  'african american': 'Black or African American',
  'afro-american': 'Black or African American',
  'negro': 'Black or African American',  // Spanish
  'preto': 'Black or African American',  // Portuguese
  'noir': 'Black or African American',   // French
  'schwarz': 'Black or African American',  // German
  'nero': 'Black or African American',   // Italian
  'siyah': 'Black or African American',  // Turkish
  // Asian
  'asian': 'Asian',
  'asiático': 'Asian',  // Spanish / Portuguese
  'asiatique': 'Asian', // French
  'asiatisch': 'Asian', // German
  'asiatico': 'Asian',  // Italian
  'asyalı': 'Asian',    // Turkish
  // Hispanic/Latino
  'hispanic': 'Hispanic or Latino',
  'latino': 'Hispanic or Latino',
  'latina': 'Hispanic or Latino',
  'hispano': 'Hispanic or Latino',
  'hispánico': 'Hispanic or Latino',
  'latinoamericano': 'Hispanic or Latino',
  'hispanique': 'Hispanic or Latino',  // French
  // Native American
  'native american': 'Native American',
  'american indian': 'Native American',
  'indigenous': 'Native American',
  'indígena': 'Native American',  // Spanish/Portuguese
  // Middle Eastern
  'middle eastern': 'Middle Eastern',
  'arab': 'Middle Eastern',
  'árabe': 'Middle Eastern',  // Spanish/Portuguese
  // Pacific Islander
  'pacific islander': 'Pacific Islander',
  'isleño del pacífico': 'Pacific Islander',  // Spanish
  // Mixed/Other
  'mixed': 'Other',
  'multiracial': 'Other',
  'other': 'Other',
  'otro': 'Other',   // Spanish
  'outro': 'Other',  // Portuguese
  'autre': 'Other',  // French
  'andere': 'Other', // German
  'altro': 'Other',  // Italian
}

// ============================================================================
// Canonical Value Sets
// ============================================================================

export const DEMOGRAPHIC_CANONICAL_VALUES = {
  gender: ['Male', 'Female', 'Non-binary', VALUE_MAP_RANDOM_TARGET],
  age: [AGE_BELOW_FIRST_BAND_LABEL, '18-24', '25-34', '35-44', '45-54', '55-64', '65+', VALUE_MAP_RANDOM_TARGET],
  country: [] as string[], // Populated from COUNTRIES constant in demographics.ts
  ethnicity: [
    'White',
    'Black or African American',
    'Asian',
    'Hispanic or Latino',
    'Native American',
    'Middle Eastern',
    'Pacific Islander',
    'Other'
  ],
}

// ============================================================================
// Auto-Detect Functions
// ============================================================================

/**
 * Auto-detect gender value mappings from distinct column values.
 * Uses comprehensive multilingual token sets to map values like:
 * - "Male", "Hombre", "Männlich" → "Male"
 * - "Female", "Mujer", "Weiblich" → "Female"
 * - Coded values like "1", "2" → "" (empty, requires user input or LLM)
 */
export function autoDetectGenderValueMap(values: string[]): Record<string, string> {
  const map: Record<string, string> = {}
  for (const v of values) {
    if (v === VALUE_MAP_BLANK_SENTINEL) {
      map[v] = VALUE_MAP_RANDOM_TARGET
      continue
    }
    const lower = v.toLowerCase().trim()
    if (MALE_TOKENS.has(lower)) {
      map[v] = 'Male'
    } else if (FEMALE_TOKENS.has(lower)) {
      map[v] = 'Female'
    } else if (NON_BINARY_TOKENS.has(lower)) {
      map[v] = 'Non-binary'
    } else {
      map[v] = VALUE_MAP_RANDOM_TARGET
    }
  }
  return map
}

/**
 * The declared band for a numeric age. Ages 0–12 stay unmapped so SPSS-style
 * codes are not treated as children. Ages 13–17 are the under-18 band.
 */
function bandForNumericAge(age: number): string | null {
  if (age >= UNDER_18_MIN_AGE && age <= 17) {
    return AGE_BELOW_FIRST_BAND_LABEL
  }
  if (age >= 18 && age <= 24) {
    return '18-24'
  }
  if (age >= 25 && age <= 34) {
    return '25-34'
  }
  if (age >= 35 && age <= 44) {
    return '35-44'
  }
  if (age >= 45 && age <= 54) {
    return '45-54'
  }
  if (age >= 55 && age <= 64) {
    return '55-64'
  }
  if (age >= 65) {
    return '65+'
  }
  return null
}

/**
 * Auto-detect age value mappings from distinct column values.
 * Handles:
 * - Age buckets already in canonical format: "18-24" → "18-24", "Under 18" → "Under 18"
 * - Numeric ages: "23" → "18-24", "15" → "Under 18", "47" → "45-54"
 * - Ranges wholly inside 13–17: "13-17" → "Under 18"
 * - Birth years (1900–current year): "1990" → age computed from current year
 * - Codes 0–12 and unrecognized values → Sample from distribution
 */
export function autoDetectAgeValueMap(values: string[]): Record<string, string> {
  const map: Record<string, string> = {}
  const BUCKETS = DEMOGRAPHIC_CANONICAL_VALUES.age
  const currentYear = new Date().getFullYear()

  for (const v of values) {
    if (v === VALUE_MAP_BLANK_SENTINEL) {
      map[v] = VALUE_MAP_RANDOM_TARGET
      continue
    }
    const trimmed = v.trim()

    // Already a bucket?
    if (BUCKETS.includes(trimmed)) {
      map[v] = trimmed
      continue
    }
    if (trimmed.toLowerCase() === AGE_BELOW_FIRST_BAND_LABEL.toLowerCase()) {
      map[v] = AGE_BELOW_FIRST_BAND_LABEL
      continue
    }

    const rangeMatch = trimmed.replace(/ to /i, '-').match(AGE_RANGE_PATTERN)
    if (rangeMatch) {
      const lo = parseInt(rangeMatch[1], 10)
      const hi = parseInt(rangeMatch[2], 10)
      if (lo >= UNDER_18_MIN_AGE && hi <= 17 && lo <= hi) {
        map[v] = AGE_BELOW_FIRST_BAND_LABEL
        continue
      }
      // A range that starts under 18 but is not wholly inside 13–17 stays
      // unmapped. Falling through would parse only the low end ("16-19" → 16).
      if (lo < 18) {
        map[v] = VALUE_MAP_RANDOM_TARGET
        continue
      }
    }

    // Try parse as integer and map to bucket
    const parsed = parseInt(trimmed, 10)
    if (!isNaN(parsed)) {
      // Birth year: convert to age first
      const age = parsed >= 1900 && parsed <= currentYear ? currentYear - parsed : parsed
      map[v] = bandForNumericAge(age) ?? VALUE_MAP_RANDOM_TARGET
    } else {
      map[v] = VALUE_MAP_RANDOM_TARGET
    }
  }
  return map
}

/**
 * Auto-detect country value mappings from distinct column values.
 * Uses comprehensive alias table covering:
 * - English names: "United States" → "US"
 * - Native names: "Brasil", "Deutschland" → "BR", "DE"
 * - Common abbreviations: "USA", "UK" → "US", "GB"
 * - Already-ISO codes: "US" → "US"
 *
 * Also handles city/region-format values with embedded country info:
 * - "Greenwood, Indiana, United States" → "US" (right-to-left comma parsing)
 * - "São Paulo, Brasil" → "BR"
 * This enables the same column to be mapped to country even when it contains
 * city-level data — country codes are extracted via comma-segment fallback.
 */
export function autoDetectCountryValueMap(values: string[]): Record<string, string> {
  const map: Record<string, string> = {}

  for (const v of values) {
    if (v === VALUE_MAP_BLANK_SENTINEL) {
      map[v] = VALUE_MAP_RANDOM_TARGET
      continue
    }
    const lower = v.toLowerCase().trim()
    const trimmed = v.trim()

    // Direct alias match (fastest path)
    const code = COUNTRY_ALIASES[lower]
    if (code) {
      map[v] = code
      continue
    }

    // Already ISO-2 (2 uppercase letters)?
    if (/^[A-Z]{2}$/.test(trimmed)) {
      map[v] = trimmed
      continue
    }

    // Comma-segment fallback: parse "City, State, Country" right-to-left
    // This handles city/region columns being assigned to country
    const segments = trimmed.split(',').map((s) => s.trim()).filter(Boolean)
    if (segments.length > 1) {
      let found = ''
      for (let i = segments.length - 1; i >= 0; i--) {
        const segLower = segments[i].toLowerCase()
        const segCode = COUNTRY_ALIASES[segLower]
        if (segCode) {
          found = segCode
          break
        }
        if (/^[A-Z]{2}$/.test(segments[i])) {
          found = segments[i]
          break
        }
      }
      map[v] = found
    } else {
      map[v] = VALUE_MAP_RANDOM_TARGET
    }
  }
  return map
}

/**
 * Auto-detect ethnicity value mappings from distinct column values.
 * Covers multilingual variants:
 * - "White", "Blanco", "Weiß" → "White"
 * - "Black", "Negro", "Schwarz" → "Black or African American"
 * - "Asian", "Asiático" → "Asian"
 */
export function autoDetectEthnicityValueMap(values: string[]): Record<string, string> {
  const map: Record<string, string> = {}

  for (const v of values) {
    const lower = v.toLowerCase().trim()
    const canonical = ETHNICITY_ALIASES[lower]
    map[v] = canonical || '' // Empty if not recognized
  }
  return map
}

// ============================================================================
// Country Inference from Geo Siblings (city/region/state)
// ============================================================================

/** Canonical IDs for geographic columns that may contain embedded country info. */
const GEO_SIBLING_CANONICAL_IDS = new Set([
  'std_geo_city',
  'std_geo_region',
])

/** Column name patterns that indicate geographic data (for unmapped columns). */
const GEO_SIBLING_NAME_PATTERNS =
  /(?:^|[\s_-])(city|town|municipality|metro|state|province|region|location|district|county|area)(?:$|[\s_-])/i

/**
 * Check if a canonical ID or column name indicates a geographic sibling column
 * that could contain embedded country information.
 */
export function isGeoSiblingColumn(canonicalId: string | null, columnName: string): boolean {
  if (canonicalId && GEO_SIBLING_CANONICAL_IDS.has(canonicalId)) return true
  return GEO_SIBLING_NAME_PATTERNS.test(columnName)
}

/**
 * Infer ISO country codes from geographic column values (city/region/state).
 *
 * Parses comma-separated values right-to-left, checking each segment against
 * COUNTRY_ALIASES for strict exact matches. This handles common formats:
 * - "Greenwood, Indiana, United States" → "US"
 * - "San Diego, California, United States" → "US"
 * - "São Paulo, Brasil" → "BR"
 * - "Berlin, Deutschland" → "DE"
 * - "Paris" → "" (single segment, no country match — left for LLM)
 *
 * Capped at 100 distinct values to keep UI responsive.
 * Always use raw distinct values (before any city value map is applied).
 */
export function inferCountryFromGeoValues(values: string[]): Record<string, string> {
  const map: Record<string, string> = {}

  // Cap at 100 distinct values — beyond that, let LLM auto-fill handle it
  const capped = values.slice(0, 100)

  for (const v of capped) {
    const trimmed = v.trim()
    if (!trimmed) {
      map[v] = ''
      continue
    }

    // Split by comma and check segments right-to-left
    const segments = trimmed.split(',').map((s) => s.trim()).filter(Boolean)

    let found = ''
    for (let i = segments.length - 1; i >= 0; i--) {
      const segment = segments[i]
      const lower = segment.toLowerCase()

      // Strict alias match
      const code = COUNTRY_ALIASES[lower]
      if (code) {
        found = code
        break
      }

      // Already an ISO-2 code (e.g., "US", "GB")?
      if (/^[A-Z]{2}$/.test(segment)) {
        found = segment
        break
      }
    }

    map[v] = found || VALUE_MAP_RANDOM_TARGET
  }

  return map
}

// ============================================================================
// Helpers
// ============================================================================

const DEMOGRAPHIC_CANONICAL_IDS = new Set([
  'std_dem_gender',
  'std_dem_age',
  'std_geo_country',
  'std_dem_ethnicity',
])

/**
 * Returns true if the given canonicalId refers to a core demographic variable
 * (gender, age, country, ethnicity) that supports value-map auto-detection.
 */
export function isDemographicVariable(canonicalId: string): boolean {
  return DEMOGRAPHIC_CANONICAL_IDS.has(canonicalId)
}

// ============================================================================
// Generic Dispatcher
// ============================================================================

/**
 * Dispatch auto-detection based on canonical demographic ID.
 * Returns an empty object for non-demographic columns.
 */
export function autoDetectDemographicValueMap(
  canonicalId: string,
  distinctValues: string[]
): Record<string, string> {
  switch (canonicalId) {
    case 'std_dem_gender':
      return autoDetectGenderValueMap(distinctValues)
    case 'std_dem_age':
      return autoDetectAgeValueMap(distinctValues)
    case 'std_geo_country':
      return autoDetectCountryValueMap(distinctValues)
    case 'std_dem_ethnicity':
      return autoDetectEthnicityValueMap(distinctValues)
    default:
      return {}
  }
}
